import { useState } from 'react';
import { animated, useSpring, type SpringValues } from '@react-spring/web';
import { ChevronRightIcon } from '@mentimeter/ragnar-visuals';
import { blueBase, coralBase, blackBase } from '@mentimeter/ragnar-colors';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Clickable } from '@mentimeter/ragnar-ui/clickable';
import { Text } from '@mentimeter/ragnar-ui/text';
import { UpvoteIndicator } from './UpvoteIndicator';
import { getUniqueBy } from './getUniqueBy';
import { RotateOnHoverAnimation } from './RotateOnHoverAnimation';
import { GroupItemsPreview } from './GroupItemsPreview';
import { GroupTag } from './GroupTag';
import { MixShape } from './shapes/Shapes';

export interface GroupCardProps {
  title: string;
  groupShape?: React.ReactNode;
  tag?: string | undefined;
  groupId: string;
  onClick: (groupId: string) => void;
  upvotes?: number | undefined;
  isTopVoted?: boolean;
  shouldShowVotes?: boolean;
  responses: Array<{ text: string }>;
  disabled?: boolean;
  innerRef?: React.Ref<HTMLElement>;
  isPulsing?: boolean;
}

export const GroupCard = ({
  title,
  groupShape,
  tag,
  onClick,
  groupId,
  shouldShowVotes = false,
  upvotes = 0,
  isTopVoted = false,
  responses,
  disabled,
  innerRef = null,
  isPulsing = false,
}: GroupCardProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const shouldShowUpvotes = upvotes > 0 && shouldShowVotes;

  const verticalMove = 8;

  const pulseAnimation = useSpring({
    from: { y: -verticalMove },
    to: [{ y: 0 }, { y: -verticalMove }, { y: 0 }],
    config: {
      duration: 300,
    },
  });

  const uniqueResponses = shouldShowVotes
    ? []
    : getUniqueBy(responses, 'text').map((response) => response.text);

  const totalNumberOfResponses = responses.length;

  return (
    <Box ref={innerRef} height="100%" width="100%">
      <Clickable
        as={animated.button}
        height="100%"
        width="100%"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        px="space12"
        py="space14"
        borderRadius="2xl"
        onClick={() => onClick(groupId)}
        onFocus={() => setIsHovering(true)}
        onBlur={() => setIsHovering(false)}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        bg={shouldShowUpvotes ? 'secondaryWeakest' : 'neutralWeakest'}
        disabled={disabled}
        extend={({ theme }) => ({
          textAlign: 'start',
          ...(disabled
            ? {
                cursor: 'default',
                opacity: 1,
                ':hover': {
                  opacity: 1,
                },
              }
            : {
                transition: 'background 0.3s ease-out',
                ':focus-visible': {
                  background: shouldShowUpvotes
                    ? theme.colors.secondaryWeak
                    : theme.colors.neutral,
                },
                '@media (hover: hover)': {
                  ':hover': {
                    opacity: 1,
                    background: shouldShowUpvotes
                      ? theme.colors.secondaryWeak
                      : theme.colors.neutral,
                  },
                },
              }),
        })}
        style={isPulsing ? (pulseAnimation as SpringValues) : {}}
      >
        <Box width="15%">
          <RotateOnHoverAnimation isHovering={isHovering}>
            {groupShape ? (
              groupShape
            ) : (
              <MixShape fillColors={[blueBase, coralBase, blackBase]} />
            )}
          </RotateOnHoverAnimation>
        </Box>
        <Box
          flex="1 1 auto"
          ml="space4"
          minHeight="156px"
          justifyContent="space-evenly"
        >
          <Box
            width="100%"
            flexDirection="row"
            alignItems="center"
            flexWrap="wrap"
          >
            <Text
              fontSize={7}
              fontWeight="regular"
              fontFamily="heading"
              color="text"
              mr="space4"
              style={{
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              {title}
            </Text>
            {tag && <GroupTag>{tag}</GroupTag>}
          </Box>
          {shouldShowUpvotes && (
            <UpvoteIndicator
              upvotes={upvotes}
              isTopVoted={isTopVoted}
              upvotesFontSize="48px"
            />
          )}
          {!shouldShowVotes && totalNumberOfResponses > 0 && (
            <GroupItemsPreview
              uniqueResponses={uniqueResponses}
              totalNumberOfResponses={totalNumberOfResponses}
            />
          )}
        </Box>
        {!disabled && (
          <Box>
            <ChevronRightIcon size={5} color="text" />
          </Box>
        )}
      </Clickable>
    </Box>
  );
};
